import { useCallback, useEffect, useState } from "react";

// Per-user, per-browser theme override for staff users. Stored in
// localStorage so it survives reloads but stays personal to the user
// (we deliberately do NOT round-trip this to the server — every staff
// member can pick their own colour without affecting their colleagues
// or the org-wide branding controlled by admins).
const STORAGE_KEY = "staff-theme-primary";

export const STAFF_THEME_PRESETS: { name: string; value: string }[] = [
  { name: "Oakland Teal", value: "#0c3547" },
  { name: "Sky",          value: "#1fa8e0" },
  { name: "Indigo",       value: "#4f46e5" },
  { name: "Violet",       value: "#7c3aed" },
  { name: "Rose",         value: "#e11d48" },
  { name: "Amber",        value: "#d97706" },
  { name: "Emerald",      value: "#059669" },
  { name: "Slate",        value: "#475569" },
];

export const STAFF_THEME_DEFAULT = STAFF_THEME_PRESETS[0].value;

function readStored(): string | null {
  // Guard for SSR / pre-render where `window` (and thus localStorage)
  // is undefined — without this the useState initializer throws on the
  // first server-side render.
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && /^#[0-9a-fA-F]{6}$/.test(v) ? v : null;
  } catch {
    return null;
  }
}

export function useStaffTheme() {
  const [primary, setPrimaryState] = useState<string>(() => readStored() ?? STAFF_THEME_DEFAULT);

  // Cross-tab sync: if the user changes their colour in one tab, an
  // open chat in another tab should pick it up on next render.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setPrimaryState(readStored() ?? STAFF_THEME_DEFAULT);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setPrimary = useCallback((next: string) => {
    if (!/^#[0-9a-fA-F]{6}$/.test(next)) return;
    try {
      if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, next);
    } catch {}
    setPrimaryState(next);
  }, []);

  const reset = useCallback(() => {
    try {
      if (typeof window !== "undefined") window.localStorage.removeItem(STORAGE_KEY);
    } catch {}
    setPrimaryState(STAFF_THEME_DEFAULT);
  }, []);

  return { primary, setPrimary, reset, presets: STAFF_THEME_PRESETS };
}
